import {IntegerInput} from 'platform/components';

import {KeyboardEvent, useState} from 'react';

import {isNil} from 'ramda';

import {TestIdProps} from 'shared/web';

interface DraftIntegerFieldProps extends TestIdProps {
  label: string;
  value: number;
  onCommit: (value: number) => void;
}

// `null` = the user is not editing; an edited value can itself be `null` when the field is cleared.
type Draft = {value: number | null} | null;

/**
 * Integer input that keeps typed digits as a local draft and commits them once on blur or Enter,
 * so a partially typed number (the "1" of "150") never reaches the clamping editor state. Escape
 * and an empty draft revert to the current value.
 */
export function DraftIntegerField(props: DraftIntegerFieldProps) {
  const [draft, setDraft] = useState<Draft>(null);

  const commit = () => {
    if (isNil(draft)) {
      return;
    }
    setDraft(null);
    if (isNil(draft.value) || draft.value === props.value) {
      return;
    }
    props.onCommit(draft.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      commit();
    }
    if (event.key === 'Escape' && !isNil(draft)) {
      // Escape reverts the draft only; it must not also close the editor dialog.
      event.stopPropagation();
      setDraft(null);
    }
  };

  return (
    <IntegerInput
      label={props.label}
      value={isNil(draft) ? props.value : draft.value}
      onChange={(value) => setDraft({value})}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      data-testid={props['data-testid']}
    />
  );
}
